import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, Field, Input, cn } from "../../pages/manage/ui";

const Bullets = ({ items }) => (
  <ul className="tw-space-y-1.5">
    {items.map((text) => (
      <li key={text} className="tw-flex tw-items-start tw-gap-2.5 tw-text-sm tw-text-muted-foreground">
        <span className="tw-mt-[7px] tw-size-1.5 tw-shrink-0 tw-rounded-full tw-bg-[var(--fill-stronger)]" />
        <span>{text}</span>
      </li>
    ))}
  </ul>
);

/**
 * AccountDeletionModal — three steps before an account is deactivated
 * (warning → type the phrase + tick the box → final confirmation). Drawn with
 * the account pages' kit (pages/manage/ui.js Dialog).
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to call when closing the modal
 * @param {Function} props.onConfirm - Function to call when user confirms deletion
 * @param {boolean} props.isLoading - Whether the deletion is in progress
 * @returns {JSX.Element}
 */
const AccountDeletionModal = ({ open, onClose, onConfirm, isLoading = false }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1); // 1: Warning, 2: Confirmation, 3: Final verification
  const [confirmationText, setConfirmationText] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);

  const requiredText = "DELETE MY ACCOUNT";
  const isTextValid = confirmationText === requiredText;

  const handleClose = () => {
    setStep(1);
    setConfirmationText("");
    setIsConfirmed(false);
    onClose();
  };

  const handleFinalConfirm = () => {
    if (isTextValid && isConfirmed) {
      onConfirm();
    }
  };

  const canProceed = step === 1 || (step === 2 && isTextValid && isConfirmed) || step === 3;
  const isFinalStep = step === 3;

  const titles = {
    1: t("account_delete_step1_title", { defaultValue: "Delete your account?" }),
    2: t("account_delete_step2_title", { defaultValue: "Confirm deletion" }),
    3: t("account_delete_step3_title", { defaultValue: "Final confirmation" }),
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : handleClose}
      hideClose={isLoading}
      closeOnBackdrop={false}
      title={titles[step]}
      description={t("account_delete_step_of", { step, defaultValue: "Step {{step}} of 3" })}
      footer={
        <>
          {step > 1 && (
            <Button variant="light" onClick={() => setStep(step - 1)} disabled={isLoading}>
              {t("back", { defaultValue: "Back" })}
            </Button>
          )}
          <Button
            variant={isFinalStep ? "destructive" : "default"}
            onClick={isFinalStep ? handleFinalConfirm : () => setStep(step + 1)}
            disabled={!canProceed}
            loading={isLoading}
          >
            {isLoading
              ? t("account_deactivating", { defaultValue: "Deactivating account..." })
              : isFinalStep
                ? t("account_deactivate", { defaultValue: "Deactivate account" })
                : t("continue", { defaultValue: "Continue" })}
          </Button>
        </>
      }
    >
      {step === 1 && (
        <div className="tw-flex tw-flex-col tw-gap-4">
          <p className="tw-text-[15px]">
            {t("delete_account_warning_description", {
              defaultValue:
                "Deleting your account will deactivate it immediately. You have 7 days to recover your account by logging in. After 7 days, your account and data will be permanently removed.",
            })}
          </p>
          <Bullets
            items={[
              t("delete_account_warning_1", { defaultValue: "Your account will be deactivated immediately" }),
              t("delete_account_warning_2", { defaultValue: "You have 7 days to recover your account by logging in" }),
              t("delete_account_warning_3", { defaultValue: "After 7 days, all data will be permanently deleted" }),
              t("delete_account_warning_4", { defaultValue: "Pending bookings will be cancelled after permanent deletion" }),
            ]}
          />
          <p className="tw-rounded-[var(--r-md)] tw-bg-[var(--fill)] tw-px-3.5 tw-py-3 tw-text-sm tw-font-medium">
            {t("delete_account_recovery_window", { defaultValue: "You can recover your account within 7 days by simply logging in again." })}
          </p>
        </div>
      )}

      {step === 2 && (
        <div className="tw-flex tw-flex-col tw-gap-4">
          <p className="tw-text-[15px]">
            {t("confirm_deletion_description", {
              defaultValue:
                "To confirm that you want to deactivate your account (with 7-day recovery window), please type the following text exactly as shown:",
            })}
          </p>
          <code className="tw-select-all tw-rounded-[var(--r-md)] tw-bg-[var(--fill)] tw-px-3.5 tw-py-3 tw-font-mono tw-text-base tw-font-semibold">{requiredText}</code>
          <Field
            label={t("type_confirmation_text", { defaultValue: "Type the confirmation text:" })}
            error={confirmationText && !isTextValid ? t("text_mismatch", { defaultValue: "Text does not match exactly" }) : null}
          >
            <Input value={confirmationText} onChange={(e) => setConfirmationText(e.target.value)} placeholder={requiredText} autoComplete="off" autoCapitalize="characters" invalid={!!confirmationText && !isTextValid} />
          </Field>
          <label className={cn("tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-[var(--r-md)] tw-bg-[var(--fill)] tw-p-3.5")}>
            <input type="checkbox" checked={isConfirmed} onChange={(e) => setIsConfirmed(e.target.checked)} className="tw-mt-0.5 tw-size-4 tw-shrink-0 tw-accent-[#ed2b32]" />
            <span className="tw-text-sm">
              {t("confirm_deletion_checkbox", {
                defaultValue:
                  "I understand that my account will be deactivated and I have 7 days to recover it by logging in. I want to proceed with account deactivation.",
              })}
            </span>
          </label>
        </div>
      )}

      {step === 3 && (
        <div className="tw-flex tw-flex-col tw-gap-4">
          <p className="tw-text-[15px]">
            {t("final_warning_description", {
              defaultValue:
                "Your account will be deactivated immediately. You have 7 days to recover it by logging in. After 7 days, all data will be permanently deleted.",
            })}
          </p>
          <div>
            <p className="tw-mb-2 tw-text-[15px] tw-font-medium">{t("what_will_happen", { defaultValue: "What will happen next:" })}</p>
            <Bullets
              items={[
                t("deletion_step_1", { defaultValue: "Your account will be immediately deactivated" }),
                t("deletion_step_2", { defaultValue: "You have 7 days to recover your account by logging in" }),
                t("deletion_step_3", { defaultValue: "You will be logged out and redirected to the homepage" }),
                t("deletion_step_4", { defaultValue: "After 7 days, all data will be permanently deleted" }),
                t("deletion_step_5", { defaultValue: "You will receive a confirmation email" }),
              ]}
            />
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default AccountDeletionModal;
